import { AppBar, Tabs, Tab, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useState, useMemo, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Loader, Page, PageTitleWithBack } from '../../../../components';
import { ManageAppRoles } from './manage-app-roles/manage-app-roles';
import { ManageJobTitles } from './manage-job-titles';
import { useAllowedNavRoutes, useFlags } from '../../../../hooks';
import { UserContext } from '../../../../context';
import { getCurrentNavigationArea } from '../../../../helpers/axios';

export const enum managePages {
  manageRoles,
  manageJobTitles
}
interface IManageRolesAndTitles {}

const rootPath = '/admin/user-tools/unified-user-management/manage-roles-and-titles';

export const ManageRolesAndTitles: FC<IManageRolesAndTitles> = () => {
  const featureFlags = useFlags();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);
  const [selectedPage, setSelectedPage] = useState<managePages>(null);

  const canViewJobTitles = useMemo(() => {
    let currentNavArea = getCurrentNavigationArea(`${rootPath}/job-titles`);
    return availableLinks.has(currentNavArea);
  }, [availableLinks]);

  const canViewAppRoles = useMemo(() => {
    let currentNavArea = getCurrentNavigationArea(`${rootPath}/app-roles`);
    return availableLinks.has(currentNavArea);
  }, [availableLinks]);

  useEffect(() => {
    let tabParam = location.pathname.includes('job-titles') ? 'job-titles' : 'app-roles';

    if (tabParam === 'job-titles' && !canViewJobTitles) {
      tabParam = 'app-roles';
    } else if (tabParam === 'app-roles' && !canViewAppRoles) {
      tabParam = 'job-titles';
    }

    // Set the selected page based on the current path
    setSelectedPage(
      location.pathname.includes('job-titles')
        ? managePages.manageJobTitles
        : managePages.manageRoles
    );
    history.replace(`${rootPath}/${tabParam}`);
  }, [location.pathname, canViewJobTitles, canViewAppRoles, history]);

  // Define the links for the tabs based on permissions
  const manageLinks = useMemo(
    () =>
      [
        {
          name: 'App Roles',
          value: managePages.manageRoles,
          hidden: !canViewAppRoles
        },
        {
          name: 'Job Titles',
          value: managePages.manageJobTitles,
          hidden: !canViewJobTitles
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: managePages) => {
    setSelectedPage(newValue);
    const tabParam = newValue === managePages.manageJobTitles ? 'job-titles' : 'app-roles';
    history.replace(`${rootPath}/${tabParam}`);
  };

  const title = 'Manage Roles and Titles';
  return (
    <Page title={title}>
      <PageTitleWithBack title={title} backText="Back to admin" backUrl="/admin" />
      {selectedPage === null ? (
        <Loader type="overlay" position="centered" />
      ) : (
        <>
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedPage}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChangeTab}
              variant="scrollable"
            >
              {manageLinks.map(link => {
                return link.hidden ? null : (
                  <Tab
                    value={link.value}
                    key={link.name}
                    id={`${link.name}-information`}
                    aria-controls={`${link.name}-information`}
                    label={link.name}
                  />
                );
              })}
            </Tabs>
          </AppBar>
          {selectedPage === managePages.manageRoles && canViewAppRoles && (
            <Typography component={'div'}>
              <ManageAppRoles />
            </Typography>
          )}
          {selectedPage === managePages.manageJobTitles && canViewJobTitles && (
            <Typography component={'div'}>
              <ManageJobTitles />
            </Typography>
          )}
        </>
      )}
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  }
}));
